import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import BaseButton from '../../components/common/BaseButton'
import { useNavigate } from 'react-router-dom'
import { formatDate, formatDateToYYYYMMDD } from '../../utils/utils'
import PayPalCheckout from '../../utils/PayPalCheckout'
import PayPalSubCheckout from '../../utils/PayPalSubCheckout'
import { apiRequest } from '../../utils/api'
import { notifications } from '@mantine/notifications'
import { useAuthStore } from '../../store'
import styles from '../../styles/user/Subscribe.module.scss'

const FEATURES = [
  { icon: '/assets/icons/shield.png', bg: '#EEF2FF', title: 'Safe & Secure', desc: 'Your payment info is always protected.' },
  { icon: '/assets/icons/cancel.png', bg: '#ECFDF5', title: 'Cancel Anytime', desc: 'No commitments. Cancel anytime.' },
  { icon: '/assets/icons/lightning.png', bg: '#FFF7ED', title: 'Instant Access', desc: 'Get full access right after payment.' },
  { icon: '/assets/icons/celebration.png', bg: '#FEF9C3', title: 'Loved by Kids', desc: 'Fun, engaging, and effective learning.' },
]

const Subscribe = () => {
  const navigate = useNavigate()
  const [selectedPlan, setSelectedPlan] = useState('monthly')
  const [showPayPal, setShowPayPal] = useState(false)
  const [startDate] = useState(new Date())
  const user = useAuthStore((state) => state.user)
  const updateUser = useAuthStore((state) => state.updateUser)
  const [monthEndDate] = useState(() => {
    const date = new Date()
    date.setMonth(date.getMonth() + 1)
    return date
  })
  const [yearEndDate] = useState(() => {
    const date = new Date()
    date.setFullYear(date.getFullYear() + 1)
    return date
  })

  const [promoCode, setPromoCode] = useState('')
  const [promoStatus, setPromoStatus] = useState(null)
  const [promoLoading, setPromoLoading] = useState(false)
  const [promoActivating, setPromoActivating] = useState(false)

  const handlePlanChange = (plan) => {
    setSelectedPlan(plan)
  }

  const getPrice = () => {
    return selectedPlan === 'monthly' ? '19.99' : '199.00'
  }

  const handleConfirmButton = () => {
    setShowPayPal(true)
  }

  const handlePromoValidate = async () => {
    if (!promoCode.trim()) return
    setPromoLoading(true)
    setPromoStatus(null)
    try {
      const res = await apiRequest('/promoCode/validate', 'POST', { CODE: promoCode.trim() })
      setPromoStatus(res)
    } catch {
      setPromoStatus({ valid: false, message: 'Server error. Please try again.' })
    } finally {
      setPromoLoading(false)
    }
  }

  const handlePromoActivate = async () => {
    if (!promoStatus?.valid) return
    setPromoActivating(true)
    try {
      const res = await apiRequest('/promoCode/redeem', 'POST', {
        CODE: promoCode.trim(),
        ID: user.ID,
        USER_ID: user.USER_ID,
        CENTER_ID: user.CENTER_ID,
      })
      if (res.success) {
        notifications.show({
          title: 'Access granted!',
          message: `Your ${promoStatus.duration}-month free access has been activated.`,
          color: 'green',
        })
        const refreshRes = await apiRequest('/users/refresh', 'POST', { ID: user.ID })
        if (refreshRes.success === 'Y') {
          updateUser(refreshRes.data)
        }
        navigate('/')
      } else {
        notifications.show({ title: 'Error', message: res.message || 'Failed to activate code.', color: 'red' })
      }
    } catch {
      notifications.show({ title: 'Error', message: 'Server error. Please try again.', color: 'red' })
    } finally {
      setPromoActivating(false)
    }
  }

  const saveOrderToDB = async (orderID) => {
    try {
      await apiRequest('/addOrder2', 'POST', {
        TITLE: selectedPlan,
        START: formatDateToYYYYMMDD(startDate),
        END: selectedPlan === 'monthly'
          ? formatDateToYYYYMMDD(monthEndDate)
          : formatDateToYYYYMMDD(yearEndDate),
        ID: user.ID,
        USER_ID: user.USER_ID,
        CENTER_ID: user.CENTER_ID,
        PRICE: getPrice(),
        ORDER_ID: orderID,
      })
      notifications.show({
        title: 'Purchase completed.',
        message: 'Your purchase has been completed.',
        color: 'green',
      })
      navigate('/userSignin')
    } catch (err) {
      console.error('Failed to save order.', err)
      notifications.show({
        title: 'Error',
        message: 'There was a problem processing your purchase.',
        color: 'red',
      })
    }
  }

  const plans = [
    {
      key: 'monthly',
      name: 'Monthly',
      desc: 'Billed monthly. Cancel anytime.',
      price: '$19.99',
      unit: '/ mo',
      badge: { label: '★ Most Popular', cls: styles.badgePopular },
      period: `${formatDate(formatDateToYYYYMMDD(startDate))} – ${formatDate(formatDateToYYYYMMDD(monthEndDate))}`,
    },
    {
      key: 'yearly',
      name: 'Yearly',
      desc: 'Billed yearly. Save 17%.',
      price: '$199.00',
      unit: '/ year',
      badge: { label: 'Save 17%', cls: styles.badgeSave },
      period: `${formatDate(formatDateToYYYYMMDD(startDate))} – ${formatDate(formatDateToYYYYMMDD(yearEndDate))}`,
    },
  ]

  return (
    <>
      <div className={styles.pageWrapper}>
        {/* Background */}
        <img src="/assets/login/background-5.svg" alt="" className={styles.topBg} />
        <img src="/assets/login/background-6.svg" alt="" className={styles.bottomRightBg} />

        {/* Header */}
        <header className={styles.pageHeader}>
          <Link to="/dashboard/individual" className={styles.logoLink}>
            <img src="/assets/white-logo.svg" alt="Solution M" className={styles.logoImg} />
          </Link>
        </header>

        {/* Main */}
        <div className={styles.mainContent}>
          <div className={styles.inner}>

            {/* Hero */}
            <div className={styles.heroSection}>
              <img
                src="/assets/subscribe-confetti-1.png"
                alt="fireworks"
                className={styles.fireworksImg}
              />
              <h1 className={styles.heroTitle}>
                Congratulations!<br />You're all set!
              </h1>
              <p className={styles.heroSubtitle}>
                Join our membership and unlock the full power of Solution M.
              </p>
            </div>

            {/* Plan cards */}
            <div className={styles.plansGrid}>
              {plans.map((plan) => {
                const isActive = selectedPlan === plan.key
                return (
                  <div
                    key={plan.key}
                    className={`${styles.planCard} ${isActive ? styles.planCardActive : ''}`}
                    onClick={() => handlePlanChange(plan.key)}
                  >
                    <div className={styles.planCardTop}>
                      <p className={styles.planName}>{plan.name}</p>
                      <div className={`${styles.radioCircle} ${isActive ? styles.radioCircleActive : ''}`}>
                        {isActive && <div className={styles.radioInner} />}
                      </div>
                    </div>
                    <p className={styles.planDesc}>{plan.desc}</p>
                    <div className={styles.planPriceRow}>
                      <span className={styles.planPriceBig}>{plan.price}</span>
                      <span className={styles.planPriceUnit}>{plan.unit}</span>
                    </div>
                    <span className={`${styles.planBadge} ${plan.badge.cls}`}>
                      {plan.badge.label}
                    </span>
                  </div>
                )
              })}
            </div>

            {/* Secure note */}
            <p className={styles.secureNote}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#b0b0b0" strokeWidth="2.5">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Secure payment. Cancel anytime.
            </p>

            {/* Promo code */}
            <div className={styles.promoSection}>
              <p className={styles.promoLabel}>Have a promo code?</p>
              <div className={styles.promoRow}>
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toUpperCase())
                    setPromoStatus(null)
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handlePromoValidate()}
                  placeholder="e.g. MH-A1B2C3"
                  className={styles.promoInput}
                />
                <button
                  onClick={handlePromoValidate}
                  disabled={promoLoading || !promoCode.trim()}
                  className={styles.promoButton}
                >
                  {promoLoading ? '...' : 'Apply'}
                </button>
              </div>

              {promoStatus && !promoStatus.valid && (
                <p className={styles.promoError}>{promoStatus.message}</p>
              )}

              {promoStatus?.valid && (
                <div className={styles.promoSuccessBox}>
                  <p className={styles.promoSuccessText}>
                    {promoStatus.duration}-month free access confirmed!
                  </p>
                  <button
                    onClick={handlePromoActivate}
                    disabled={promoActivating}
                    className={styles.promoActivateBtn}
                  >
                    {promoActivating ? 'Activating...' : 'Activate for Free'}
                  </button>
                </div>
              )}
            </div>

            {/* CTA */}
            <motion.div
              whileHover={{ scale: 1.015 }}
              whileTap={{ scale: 0.985 }}
              transition={{ duration: 0.15 }}
            >
              <BaseButton
                className={styles.ctaButton}
                onClick={handleConfirmButton}
                style={{ background: '#162543' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                Continue to Payment
              </BaseButton>
            </motion.div>
            <p className={styles.ctaNote}>You will be redirected to our secure checkout.</p>

            {/* Feature cards */}
            <div className={styles.featureGrid}>
              {FEATURES.map((f) => (
                <div key={f.title} className={styles.featureItem}>
                  <div className={styles.featureIconCircle} style={{ background: f.bg }}>
                    <img src={f.icon} alt={f.title} style={{ width: 28, height: 28, objectFit: 'contain' }} />
                  </div>
                  <p className={styles.featureTitle}>{f.title}</p>
                  <p className={styles.featureDesc}>{f.desc}</p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>

      {/* PayPal modal — unchanged */}
      {showPayPal && (
        <div
          style={{
            zIndex: 9999,
            position: 'fixed',
            top: 0, left: 0,
            width: '100vw', height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '450px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '16px 24px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h5 style={{ margin: 0 }}>PayPal</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                style={{ padding: '10px' }}
                onClick={() => setShowPayPal(false)}
              />
            </div>
            <div style={{ padding: '20px', height: '500px', overflowY: 'auto' }}>
              {user.CENTER_ID === 0 || !user.CENTER_ID ? (
                <PayPalSubCheckout
                  amount={getPrice()}
                  onApprove={async ({ subscriptionID }) => {
                    console.log('Subscribe.jsx PayPalSubCheckout >> [PAYPAL] orderID:', subscriptionID)
                    await saveOrderToDB(subscriptionID)
                    setShowPayPal(false)
                  }}
                />
              ) : (
                <PayPalCheckout
                  amount={getPrice()}
                  onApprove={async ({ orderID }) => {
                    console.log('Subscribe.jsx PayPalSubCheckout >> [PAYPAL] orderID:', orderID)
                    await saveOrderToDB(orderID)
                    setShowPayPal(false)
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Subscribe
